import { accordionContentClasses } from "@/components/ui-library-new/accordion/AccordionItem"
import { CardBasic } from "@/components/ui-library-new/card/CardBasic"
import { IconCircle } from "@/components/ui-library-new/icon/IconCircle"
import IframeModal from "@/components/ui-library-new/iframe/IframeModal"
import { Button } from "@/components/ui-library/button/Button"
import Richtext from "@/components/ui-library/richtext/Richtext"
import { MITT_VACCIN_CLINICS_URL } from "@/consts/external-urls"
import { stripConfigSuffix } from "@/consts/vaccination-slugs"
import { trackEvent } from "@/lib/amplitude/amplitude"
import { VACCINATION_CTA_EVENTS } from "@/lib/amplitude/vaccinationCtaEvents"
import { VaccinationClinicWithHours } from "@/storyblok/vaccinationRelations"
import { hasRichtextContent } from "@/utils/richTextToPlain"
import { MAPS_API_KEY } from "@config/config"
import * as Accordion from "@radix-ui/react-accordion"
import { ISbStoryData } from "@storyblok/react/rsc"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { MouseEventHandler, useState } from "react"
import { FiChevronDown } from "react-icons/fi"

export type ClinicStory = ISbStoryData<VaccinationClinicWithHours>

/**
 * The labels above the two optional hour blocks. A paragraph rather than a heading: this sits inside
 * a card body under the clinic name, so an <h2> here would claim a level it does not own.
 */
const openingHoursSubheadingClasses =
  "mt-4 text-[16px] font-semibold leading-[22px] tracking-[-0.045em] lg:text-[20px] lg:leading-[27px]"

export const VaccinationClinicCard = ({ story }: { story: ClinicStory }) => {
  const {
    clinic_name,
    icon,
    subtitle,
    address,
    opening_hours_content,
    deviating_opening_hours,
    extra_opening_hours,
    mittvaccin_clinic_id,
    use_redirect_to_vaccination_clinic_link
  } = story.content

  const link = `/${stripConfigSuffix(story.full_slug)}`

  const [open, setOpen] = useState(false)
  const [openGoogleMaps, setOpenGoogleMaps] = useState(false)

  const pathname = usePathname()
  // No id means no bookable clinic. Falling back to a placeholder rendered a working-looking
  // "Boka tid" that led to a MittVaccin page for a clinic that does not exist — better to not
  // offer booking at all than to offer a dead one.
  const bookingUrl = mittvaccin_clinic_id ? `${MITT_VACCIN_CLINICS_URL}/${mittvaccin_clinic_id}` : null

  const handleOnIframeButtonClick: MouseEventHandler<HTMLButtonElement> = e => {
    trackEvent(VACCINATION_CTA_EVENTS.book_cta_pressed, { page_path: pathname })
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    setOpen(true)
  }

  /**
   * `address` already ends with the clinic's locality, so it is the whole displayable address.
   * `city` is deliberately NOT appended: it holds the broader ort used for searching — Arninge
   * Station displays "Täby" but answers a search for "Stockholm" — so joining the two would render
   * "…, Täby, Stockholm". It stays in the overview's `search_criterias`, which reads the field
   * directly rather than the rendered string.
   */
  /**
   * A real destination behind the in-page map: hovering shows where it goes, and ⌘/ctrl/middle click
   * opens Google Maps proper in a new tab. A plain click keeps the reader here and opens the modal,
   * the same bargain the booking button makes.
   */
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`

  const handleOnMapClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    event.preventDefault()
    setOpenGoogleMaps(true)
  }

  const hasContentInDeviatingOpeningHours = hasRichtextContent(deviating_opening_hours)
  const hasContentInExtraOpeningHours = hasRichtextContent(extra_opening_hours)

  // One column until xl, so the tablet band gets a full-width row: no fixed min-height, and
  // padding only widens to px-10 at 2xl where the two columns are wide enough to afford it.
  return (
    <CardBasic className="h-auto px-4 pb-2 pt-4 has-[[data-state=open]]:pb-4 lg:px-6 lg:py-5 lg:has-[[data-state=open]]:pb-8 xl:min-h-[184px] xl:py-[26px] 2xl:px-10">
      {/* Accordion.Root wraps the whole card so the trigger can sit in the middle column
          while its content expands below the row. */}
      <Accordion.Root type="single" collapsible className="w-full">
        <Accordion.Item value="oppettider">
          <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-4">
            <IconCircle
              icon={icon}
              className="h-10 w-10 self-start lg:h-[70px] lg:w-[70px]"
              iconClassName="h-5 w-5 lg:h-8 lg:w-8"
            />
            <div className="flex min-w-0 flex-col gap-0 lg:gap-1">
              {use_redirect_to_vaccination_clinic_link ? (
                <Link
                  onClick={() =>
                    trackEvent(VACCINATION_CTA_EVENTS.clinic_detail_page_link_cta_clicked, { page_path: pathname })
                  }
                  href={link}
                >
                  <p className="text-[15px] font-semibold text-neutral-90 decoration-1 underline-offset-2 hover:text-primary-70 hover:underline lg:text-xl lg:leading-[27px]">
                    {clinic_name}
                  </p>
                </Link>
              ) : (
                <p className="text-[15px] font-semibold text-neutral-90 lg:text-xl lg:leading-[27px]">{clinic_name}</p>
              )}
              {subtitle && <span className="text-[15px] text-neutral-50 lg:text-xl lg:leading-[26px]">{subtitle}</span>}
              {address && (
                <a
                  href={mapsHref}
                  onClick={handleOnMapClick}
                  target="_blank"
                  rel="noreferrer"
                  className="cursor-pointer text-left text-[15px] text-neutral-50 underline decoration-1 underline-offset-2 hover:text-primary-70 hover:underline lg:text-xl lg:leading-[26px]"
                >
                  {address}
                </a>
              )}
              {opening_hours_content && (
                <Accordion.Header className="mt-1 flex">
                  <Accordion.Trigger className="group inline-flex items-center gap-0 text-[15px] font-normal text-neutral-50 lg:text-xl">
                    Öppettider
                    <FiChevronDown className="text-xl transition-transform duration-300 group-data-[state=open]:rotate-180 lg:text-3xl" />
                  </Accordion.Trigger>
                </Accordion.Header>
              )}
            </div>

            {bookingUrl && (
              <Button
                asChild
                onClick={handleOnIframeButtonClick}
                size="small"
                variant="primary"
                className="w-auto shrink-0 self-end text-sm lg:h-[43px] lg:w-[110px] lg:text-[17px]"
              >
                {/* Plain <a>, not next/link: MittVaccin is another origin, so there is no route to
                    prefetch and nothing for the router to do. */}
                <a href={bookingUrl}>
                  <span className="lg:hidden">Boka</span>
                  <span className="hidden lg:inline">Boka tid</span>
                </a>
              </Button>
            )}
          </div>

          {open && bookingUrl && (
            <IframeModal
              src={bookingUrl}
              onClose={() => {
                setOpen(false)
                setOpenGoogleMaps(false)
              }}
              useCloseButton
              modalClassName="p-0"
            />
          )}
          {openGoogleMaps && (
            <IframeModal
              // Maps Embed API rather than the undocumented `?output=embed`, whose place panel failed
              // with "Det gick inte att läsa in platsinformation" — it cannot resolve a place from
              // bare coordinates. `place` mode is given the street address instead, which resolves,
              // so the marker comes with a working info card. The host matters: a relative URL here
              // loads our own site into the frame.
              //
              // The key is referrer-restricted, so the frame only renders on allow-listed hosts.
              src={`https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${encodeURIComponent(address)}`}
              onClose={() => {
                setOpenGoogleMaps(false)
                setOpen(false)
              }}
              useCloseButton
              modalClassName="p-0"
            />
          )}

          {opening_hours_content && (
            <Accordion.Content className={accordionContentClasses}>
              <div className="pl-14 pt-3 lg:pl-[85px]">
                <CardBasic
                  variant="soft"
                  className="min-h-0 px-5 py-3 text-sm text-neutral-90 lg:px-6 lg:py-4 lg:text-base"
                >
                  <Richtext
                    className="[&_p]:text-sm lg:[&_p]:text-base [&_strong]:text-base lg:[&_strong]:text-xl"
                    key="opening_hours_content"
                    content={opening_hours_content}
                  />
                  {hasContentInDeviatingOpeningHours && (
                    <>
                      <p className={openingHoursSubheadingClasses}>Avvikande öppettider:</p>
                      <Richtext
                        className="-mt-4 [&_p]:text-sm lg:[&_p]:text-base [&_strong]:text-base lg:[&_strong]:text-xl"
                        key="deviating_opening_hours_content"
                        content={deviating_opening_hours}
                      />
                    </>
                  )}
                  {/* Below Avvikande, matching the order the sync writes into opening_hours_content
                      and the order the clinic page renders them in. */}
                  {hasContentInExtraOpeningHours && (
                    <>
                      <p className={openingHoursSubheadingClasses}>Extra öppet:</p>
                      <Richtext
                        className="-mt-4 [&_p]:text-sm lg:[&_p]:text-base [&_strong]:text-base lg:[&_strong]:text-xl"
                        key="extra_opening_hours_content"
                        content={extra_opening_hours}
                      />
                    </>
                  )}
                </CardBasic>
              </div>
            </Accordion.Content>
          )}
        </Accordion.Item>
      </Accordion.Root>
    </CardBasic>
  )
}
